/**
 * Learning tab honesty — same standard as DFM/RM after 2026-08-30 walks.
 *
 * Distilled platform learnings are visible to seats. Customer feedback rows
 * stay seat-owned. Agent chat is not the store. min_distinct_tenants stays 3.
 * Selected-app vs platform-wide is labelled. Leftover-dev is never stamped.
 * Empty is honest (empty vs gated). Missing nested fields do not crash.
 */

export const DEFAULT_MIN_DISTINCT_TENANTS = 3;
export const WEATHER_APP_ID = 'app_qkzzBGS81pZ0';
export const TREKKER_APP_ID = 'app_g2U0gH20uTO3';
export const CORE_APP_ID = 'app_5UxnQWMZZ2Fa';

const LEFTOVER_DEV_TENANT = /leftover[-_]?dev/i;
const CORE_APP_IDS = new Set([
  'control-plane',
  'devcockpit-core',
  'farmer-portal',
  CORE_APP_ID,
  '',
]);

export type LearningItem = {
  id: string | null;
  category?: string;
  label?: string;
  agent_id?: string | null;
  distinct_tenant_count?: number | null;
  tenant_basis?: string | null;
  tenant_label?: string | null;
  created_at?: string | null;
  status?: string | null;
  text: string;
};

export type LearningScopeKind = 'platform_wide' | 'selected_app';
export type LearningEmptyKind = 'empty' | 'gated' | 'unavailable' | null;

export type LearningScope = {
  kind: LearningScopeKind;
  app_id: string | null;
  app_name: string | null;
  label: string;
  note: string;
};

export type LearningGate = {
  min_distinct_tenants: number;
  current_distinct_count: number;
  met: boolean;
  note: string;
};

export type DirtyLearningSummary = {
  ok?: boolean;
  learnings?: LearningItem[] | null;
  feedback?: {
    total?: number;
    last_24h?: number;
    top_apps?: Array<{ app_id: string; count: number }>;
    latest_timestamp?: string | null;
  } | null;
  gate?: {
    min_distinct_tenants?: number;
    current_distinct_count?: number;
    met?: boolean;
    note?: string;
  } | null;
  scope?: Partial<LearningScope> | null;
  message?: string | null;
  empty_kind?: LearningEmptyKind;
  store?: string;
  omitted?: { customer_feedback_rows?: boolean; top_apps?: boolean };
  loop_health?: {
    scope?: string;
    feedback_total?: number;
    last_24h?: number;
    latest_timestamp?: string | null;
    note?: string;
  } | null;
};

export type LearningLoopHealth = {
  scope: 'platform_wide';
  feedback_total: number;
  last_24h: number;
  latest_timestamp: string | null;
  note: string;
};

export type LearningView = {
  ok: boolean;
  store: string;
  scope: LearningScope;
  gate: LearningGate;
  learnings: LearningItem[];
  count: number;
  empty_kind: LearningEmptyKind;
  empty_message: string | null;
  tenant_id: string | null;
  subtitle: string;
  loop_health: LearningLoopHealth;
};

export function normalizeTenantId(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const tenant = String(value).trim();
  return tenant ? tenant : null;
}

export function isLeftoverDevTenant(value: unknown): boolean {
  return LEFTOVER_DEV_TENANT.test(String(value || ''));
}

export function isLearningCoreApp(appId?: string | null, appName?: string | null): boolean {
  const id = String(appId || '').trim();
  if (!id || CORE_APP_IDS.has(id)) return true;
  return /dev\s*cockpit\s*core/i.test(String(appName || ''));
}

/** Never invent leftover-dev or a hardcoded prod/dev fallback. */
export function resolveLearningTenant(input: {
  selectedTenant?: string | null;
  hostedTenant?: string | null;
}): string | null {
  for (const candidate of [input.selectedTenant, input.hostedTenant]) {
    const tenant = normalizeTenantId(candidate);
    if (tenant && !isLeftoverDevTenant(tenant)) return tenant;
  }
  return null;
}

export function buildLearningScope(input: {
  selectedAppId?: string | null;
  selectedAppName?: string | null;
}): LearningScope {
  const appId = String(input.selectedAppId || '').trim();
  const appName = String(input.selectedAppName || '').trim() || null;
  if (!appId || isLearningCoreApp(appId, appName)) {
    return {
      kind: 'platform_wide',
      app_id: null,
      app_name: appName,
      label: 'Platform-wide',
      note: 'Platform-wide distilled learnings — visible to all app seats. Customer data stays seat-owned. Agent chat is not the learning store. Cross-app PIM still needs a grant.',
    };
  }
  return {
    kind: 'selected_app',
    app_id: appId,
    app_name: appName,
    label: `Selected app: ${appName || appId}`,
    note: `Platform-wide distilled learnings ${appName || appId} may see. This is not ${appName || appId} customer data. Customer rows stay seat-owned. Agent chat is not the learning store. Cross-app PIM still needs a grant.`,
  };
}

export function buildLearningGate(gate?: DirtyLearningSummary['gate'] | null): LearningGate {
  const minRaw = Number(gate?.min_distinct_tenants);
  const minDistinct =
    Number.isFinite(minRaw) && minRaw >= DEFAULT_MIN_DISTINCT_TENANTS
      ? minRaw
      : DEFAULT_MIN_DISTINCT_TENANTS;
  const currentRaw = Number(gate?.current_distinct_count);
  const currentDistinct = Number.isFinite(currentRaw) && currentRaw >= 0 ? currentRaw : 0;
  const met = currentDistinct >= minDistinct;
  return {
    min_distinct_tenants: minDistinct,
    current_distinct_count: currentDistinct,
    met,
    note: met
      ? `Promotion gate is met (${currentDistinct} distinct apps / min ${minDistinct}). New promotions can fire. The gate is not lowered.`
      : `Promotion gate is not met (${currentDistinct} distinct apps / min ${minDistinct}). Already-distilled learnings are still listed. New promotions will not fire until the gate is met. min_distinct_tenants is not lowered.`,
  };
}

const HAND_AUTHORED_SOURCE = /template_audits_analysis|stress_test_|race_condition_test_|memory-seed|^manual$|learning:seed_/i;

export function presentTenantCell(row: {
  distinct_tenant_count?: number | null;
  tenant_basis?: string | null;
  tenant_label?: string | null;
  id?: string | null;
  source?: unknown;
} | null | undefined): { label: string; basis: string; count: number | null } {
  const countRaw = Number(row?.distinct_tenant_count);
  const count = Number.isFinite(countRaw) && countRaw >= 0 ? countRaw : null;
  if (count != null) {
    return { label: String(count), basis: String(row?.tenant_basis || 'stored'), count };
  }
  const labelled = String(row?.tenant_label || '').trim();
  if (labelled && labelled !== '—') {
    return { label: labelled, basis: String(row?.tenant_basis || 'none'), count: null };
  }
  const haystack = `${row?.id || ''} ${typeof row?.source === 'string' ? row.source : ''}`;
  if (HAND_AUTHORED_SOURCE.test(haystack) || row?.tenant_basis === 'hand_authored') {
    return { label: 'hand-authored', basis: 'hand_authored', count: null };
  }
  return { label: 'no tenant evidence', basis: String(row?.tenant_basis || 'none'), count: null };
}

function projectRow(row: LearningItem | null | undefined): LearningItem | null {
  if (!row) return null;
  const text = String(row.text || '').trim();
  const tenants = presentTenantCell(row);
  return {
    id: row.id || null,
    category: row.category || row.label || 'general',
    agent_id: row.agent_id || null,
    distinct_tenant_count: tenants.count,
    tenant_basis: tenants.basis,
    tenant_label: tenants.label,
    created_at: row.created_at || null,
    status: row.status || null,
    text,
  };
}

export function presentLoopHealth(raw?: DirtyLearningSummary['loop_health'] | DirtyLearningSummary['feedback'] | null): LearningLoopHealth {
  const totalRaw = Number((raw as { feedback_total?: number; total?: number } | null)?.feedback_total
    ?? (raw as { total?: number } | null)?.total);
  const last24Raw = Number((raw as { last_24h?: number } | null)?.last_24h);
  return {
    scope: 'platform_wide',
    feedback_total: Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : 0,
    last_24h: Number.isFinite(last24Raw) && last24Raw >= 0 ? last24Raw : 0,
    latest_timestamp: (raw as { latest_timestamp?: string | null } | null)?.latest_timestamp || null,
    note: 'Platform-wide feedback volume. Not selected-app customer rows. top_apps omitted.',
  };
}

export function emptyLearningMessage(gate: LearningGate): string {
  return gate.met
    ? 'No distilled platform learnings on this environment yet. The promotion gate is met; the loop has not promoted insights here.'
    : 'No distilled platform learnings on this environment yet. Promotion requires min_distinct_tenants distinct real apps in the 90-day window. The gate is not lowered.';
}

/**
 * Present the Learning tab. Strips customer feedback / top_apps even if a
 * leftover payload still carries them. Does not throw on thin or dirty input.
 */
export function presentLearningView(input: {
  payload?: DirtyLearningSummary | null;
  selectedAppId?: string | null;
  selectedAppName?: string | null;
  selectedTenant?: string | null;
  hostedTenant?: string | null;
}): LearningView {
  const payload = input.payload && typeof input.payload === 'object' ? input.payload : {};
  const scope = buildLearningScope({
    selectedAppId: input.selectedAppId ?? payload.scope?.app_id,
    selectedAppName: input.selectedAppName ?? payload.scope?.app_name,
  });
  const gate = buildLearningGate(payload.gate);
  const learnings = Array.isArray(payload.learnings)
    ? payload.learnings.map(projectRow).filter((row): row is LearningItem => Boolean(row && row.text))
    : [];
  const emptyKind: LearningEmptyKind =
    payload.empty_kind === 'unavailable'
      ? 'unavailable'
      : learnings.length === 0
        ? gate.met
          ? 'empty'
          : 'gated'
        : null;
  const emptyMessage =
    emptyKind === 'unavailable'
      ? 'Learning summary unavailable on this environment.'
      : emptyKind
        ? payload.message || emptyLearningMessage(gate)
        : null;

  return {
    ok: payload.ok !== false,
    store: payload.store || 'agent_definitions.operational_memory.learnings',
    scope,
    gate,
    learnings,
    count: learnings.length,
    empty_kind: emptyKind,
    empty_message: emptyMessage,
    tenant_id: resolveLearningTenant({
      selectedTenant: input.selectedTenant,
      hostedTenant: input.hostedTenant,
    }),
    subtitle: `${scope.label}. ${scope.note}`,
    loop_health: presentLoopHealth(payload.loop_health),
  };
}

/** True when a presented view still carries another seat's customer row. */
export function learningViewLeaksCustomerApp(
  view: LearningView,
  otherAppId: string
): boolean {
  const haystack = JSON.stringify({
    learnings: view.learnings,
    gate: view.gate,
    empty_message: view.empty_message,
    store: view.store,
  });
  return haystack.includes(otherAppId);
}
